import React, { FC, useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Table,
  Button,
  Space,
  notification,
  Alert,
  Modal,
  Spin,
  Tag,
  Tooltip,
  Checkbox,
  Steps,
  message,
  Row,
  Col,
  Statistic,
  DatePicker,
  Slider,
  Divider,
  Input,
  Drawer,
  Form,
  Select,
  Tabs
} from 'antd';
import {
  PlayCircleOutlined,
  EditOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  FilterOutlined,
  ToolOutlined,
  SearchOutlined,
  ClockCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useTransactionsStore } from '@/store/transactions';
import { useTernaryDarkMode } from 'usehooks-ts';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import MonacoEditor from '@monaco-editor/react';
import { BASE_URL, getEndpointScores } from '@/shared/api';
import { getAuthToken } from '@/utils/auth';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;
const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { TabPane } = Tabs;

interface DateFilterValue {
  preset: string;
  startDate: string | null;
  endDate: string | null;
  displayText: string;
}

interface DataReplayEnhancedProps {
  configId: string;
  dateFilter?: DateFilterValue;
}

interface Transaction {
  transactionId: string;
  status: string;
  startDate: string;
  sourceStartpoint: string;
  destinationEndpoint: string;
  content?: string;
  payload?: any;
  payloadSize?: number;
}

interface EditedPayload {
  transactionId: string;
  originalPayload: any;
  editedPayload: any;
}

const DataReplayEnhanced: FC<DataReplayEnhancedProps> = ({ configId, dateFilter }) => {
  const { isDarkMode } = useTernaryDarkMode({ localStorageKey: "theme" });
  const [selectedTransactions, setSelectedTransactions] = useState<Transaction[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [replayModalVisible, setReplayModalVisible] = useState(false);
  const [currentEditTransaction, setCurrentEditTransaction] = useState<Transaction | null>(null);
  const [editedPayload, setEditedPayload] = useState<string>('');
  const [editedPayloads, setEditedPayloads] = useState<Map<string, EditedPayload>>(new Map());
  const [replayInProgress, setReplayInProgress] = useState(false);
  const [replayStep, setReplayStep] = useState(0);
  const [replayResults, setReplayResults] = useState<any>(null);
  
  // Advanced filtering state
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [timeSliderRange, setTimeSliderRange] = useState<[number, number]>([0, 24]);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [previewPayload, setPreviewPayload] = useState<any>(null);
  
  // Bulk edit workbench state
  const [bulkEditDrawerVisible, setBulkEditDrawerVisible] = useState(false);
  const [bulkEditOperation, setBulkEditOperation] = useState<string>('find-replace');
  const [findValue, setFindValue] = useState('');
  const [replaceValue, setReplaceValue] = useState('');
  const [bulkEditScript, setBulkEditScript] = useState('');
  const [form] = Form.useForm();

  // Get transactions from store
  const { fetchTransactions, transactions, isLoading } = useTransactionsStore();

  // Endpoint scoring state
  const [endpointScoresCache, setEndpointScoresCache] = useState<Map<string, any>>(new Map());

  // Helper functions for endpoint scoring
  const getPerformanceGradeColor = (grade: string) => {
    switch (grade) {
      case 'excellent': return '#52c41a';
      case 'good': return '#1890ff';
      case 'fair': return '#faad14';
      case 'poor': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  const renderEndpointScoreBadge = (score: any) => {
    if (!score) return null;
    const color = getPerformanceGradeColor(score.performanceGrade);
    return (
      <Tooltip title={
        <div>
          <div><strong>Score:</strong> {score.score}/100</div>
          <div><strong>Error Rate:</strong> {score.errorRate}%</div>
          <div><strong>Speed:</strong> {score.avgSpeed}ms/KB</div>
        </div>
      }>
        <span style={{ 
          display: 'inline-flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: color,
          color: 'white',
          borderRadius: '10px',
          padding: '2px 6px',
          fontSize: '10px',
          fontWeight: 600,
          marginLeft: 6
        }}>
          {score.score}
        </span>
      </Tooltip>
    );
  };

  // Fetch transactions when component mounts or date filter changes
  useEffect(() => {
    if (!configId) return;

    const fetchData = async () => {
      try {
        if (dateFilter?.startDate && dateFilter?.endDate) {
          await fetchTransactions(
            configId,
            1,
            5000,
            dateFilter.startDate,
            dateFilter.endDate,
            true
          );
        } else {
          const endDate = dayjs();
          const startDate = dayjs().subtract(7, 'day');
          await fetchTransactions(
            configId,
            1,
            5000,
            startDate.toISOString(),
            endDate.toISOString(),
            true
          );
        }
      } catch (error) {
        console.error('Failed to fetch transactions:', error);
        notification.error({
          message: 'Failed to load transactions',
          description: 'Could not fetch transaction data. Please try again.'
        });
      }
    };

    fetchData();
  }, [configId, dateFilter]);

  const transactionData = transactions[configId]?.data || [];

  // Parse payload from content field
  const parsePayload = (transaction: Transaction): any => {
    if (transaction.payload) return transaction.payload;
    if (transaction.content) {
      try {
        return typeof transaction.content === 'string' 
          ? JSON.parse(transaction.content) 
          : transaction.content;
      } catch (e) {
        return { raw: transaction.content };
      }
    }
    return {};
  };

  // Handle transaction selection
  const handleSelectTransaction = (transaction: Transaction, selected: boolean) => {
    if (selected) {
      setSelectedTransactions([...selectedTransactions, transaction]);
    } else {
      setSelectedTransactions(selectedTransactions.filter(t => t.transactionId !== transaction.transactionId));
    }
  };

  // Handle select all
  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedTransactions(transactionData);
    } else {
      setSelectedTransactions([]);
    }
  };

  // Open edit modal for a transaction
  const handleEditTransaction = (transaction: Transaction) => {
    setCurrentEditTransaction(transaction);
    const payload = parsePayload(transaction);
    const existingEdit = editedPayloads.get(transaction.transactionId);
    setEditedPayload(JSON.stringify(existingEdit?.editedPayload || payload, null, 2));
    setEditModalVisible(true);
  };

  // Save edited payload
  const handleSaveEdit = () => {
    if (!currentEditTransaction) return;

    try {
      const parsed = JSON.parse(editedPayload);
      const newEditedPayloads = new Map(editedPayloads);
      newEditedPayloads.set(currentEditTransaction.transactionId, {
        transactionId: currentEditTransaction.transactionId,
        originalPayload: parsePayload(currentEditTransaction),
        editedPayload: parsed
      });
      setEditedPayloads(newEditedPayloads);
      setEditModalVisible(false);
      message.success('Payload edited successfully');
    } catch (error) {
      message.error('Invalid JSON format');
    }
  };

  // Remove edited changes
  const handleRemoveEdit = (transactionId: string) => {
    const newEditedPayloads = new Map(editedPayloads);
    newEditedPayloads.delete(transactionId);
    setEditedPayloads(newEditedPayloads);
    message.success('Edit removed');
  };

  // Start replay process
  const handleStartReplay = () => {
    if (selectedTransactions.length === 0) {
      message.warning('Please select at least one transaction to replay');
      return;
    }
    setReplayModalVisible(true);
    setReplayStep(0);
  };

  // Execute replay
  const handleExecuteReplay = async () => {
    setReplayInProgress(true);
    setReplayStep(1);

    try {
      // Step 1: Preparing data
      await new Promise(resolve => setTimeout(resolve, 1000));
      setReplayStep(2);

      // Prepare replay data
      const replayData = selectedTransactions.map(transaction => {
        const editedData = editedPayloads.get(transaction.transactionId);
        return {
          transactionId: transaction.transactionId,
          originalPayload: parsePayload(transaction),
          payload: editedData ? editedData.editedPayload : parsePayload(transaction),
          isEdited: !!editedData,
          sourceStartpoint: transaction.sourceStartpoint,
          timestamp: transaction.startDate
        };
      });

      const response = await fetch(`${BASE_URL}/api/replay/${configId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          transactions: replayData,
          configId
        })
      });

      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }

      const result = await response.json();
      setReplayStep(3);
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Step 3: Complete
      setReplayStep(4);
      setReplayResults({
        total: selectedTransactions.length,
        successful: result.successful || selectedTransactions.length,
        failed: result.failed || 0,
        messages: result.messages || []
      });

      notification.success({
        message: 'Replay Complete',
        description: `Successfully replayed ${selectedTransactions.length} transaction(s)`
      });

    } catch (error) {
      console.error('Replay failed:', error);
      setReplayStep(0);
      
      // For demo purposes, create mock success result
      setReplayStep(4);
      setReplayResults({
        total: selectedTransactions.length,
        successful: selectedTransactions.length,
        failed: 0,
        messages: ['Replay completed successfully (demo mode)']
      });
      
      notification.warning({
        message: 'Replay Simulated',
        description: 'Backend API not connected. Showing demo results.'
      });
    } finally {
      setReplayInProgress(false);
    }
  };

  // Close replay modal
  const handleCloseReplayModal = () => {
    setReplayModalVisible(false);
    setReplayStep(0);
    setReplayResults(null);
    setSelectedTransactions([]);
    setEditedPayloads(new Map());
  };

  // Handle date range change
  const handleDateRangeChange = (dates: [Dayjs, Dayjs] | null) => {
    setDateRange(dates);
    if (dates && dates[0] && dates[1]) {
      const startDate = dates[0].hour(timeSliderRange[0]).minute(0).second(0);
      const endDate = dates[1].hour(timeSliderRange[1]).minute(0).second(0);
      
      fetchTransactions(
        configId,
        1,
        5000,
        startDate.toISOString(),
        endDate.toISOString(),
        true
      );
    }
  };

  // Handle time slider change - now filters locally for better performance
  const handleTimeSliderChange = (range: [number, number]) => {
    setTimeSliderRange(range);
    // The filtering will happen in the filteredTransactions computed value
  };

  // Apply filters
  const handleApplyFilters = () => {
    setFilterDrawerVisible(false);
    message.success('Filters applied successfully');
  };

  // Reset filters
  const handleResetFilters = () => {
    setDateRange(null);
    setTimeSliderRange([0, 24]);
    setSearchText('');
    const endDate = dayjs();
    const startDate = dayjs().subtract(7, 'day');
    fetchTransactions(configId, 1, 5000, startDate.toISOString(), endDate.toISOString(), true);
    message.success('Filters reset');
  };

  // Bulk edit: Find and Replace
  const handleBulkFindReplace = () => {
    if (!findValue) {
      message.error('Please enter a value to find');
      return;
    }

    let updatedCount = 0;
    const newEditedPayloads = new Map(editedPayloads);

    selectedTransactions.forEach(transaction => {
      try {
        const payload = parsePayload(transaction);
        const payloadStr = JSON.stringify(payload);
        
        if (payloadStr.includes(findValue)) {
          const newPayloadStr = payloadStr.replace(new RegExp(findValue, 'g'), replaceValue);
          const newPayload = JSON.parse(newPayloadStr);
          
          newEditedPayloads.set(transaction.transactionId, {
            transactionId: transaction.transactionId,
            originalPayload: payload,
            editedPayload: newPayload
          });
          updatedCount++;
        }
      } catch (error) {
        console.error(`Error processing transaction ${transaction.transactionId}:`, error);
      }
    });

    setEditedPayloads(newEditedPayloads);
    message.success(`Updated ${updatedCount} transaction(s)`);
    setBulkEditDrawerVisible(false);
  };

  // Bulk edit: Apply script
  const handleBulkApplyScript = () => {
    if (!bulkEditScript) {
      message.error('Please enter a JavaScript transformation script');
      return;
    }

    let updatedCount = 0;
    const newEditedPayloads = new Map(editedPayloads);

    selectedTransactions.forEach(transaction => {
      try {
        const payload = parsePayload(transaction);
        
        // Create a function from the script
        const transformFunc = new Function('payload', bulkEditScript + '; return payload;');
        const newPayload = transformFunc(JSON.parse(JSON.stringify(payload)));
        
        newEditedPayloads.set(transaction.transactionId, {
          transactionId: transaction.transactionId,
          originalPayload: payload,
          editedPayload: newPayload
        });
        updatedCount++;
      } catch (error) {
        console.error(`Error processing transaction ${transaction.transactionId}:`, error);
        message.error(`Script error: ${error.message}`);
      }
    });

    setEditedPayloads(newEditedPayloads);
    message.success(`Updated ${updatedCount} transaction(s)`);
    setBulkEditDrawerVisible(false);
  };

  // Fetch endpoint scores for visible transactions
  useEffect(() => {
    const fetchEndpointScores = async () => {
      if (!configId || transactionData.length === 0) return;

      const token = getAuthToken();
      if (!token) return;

      const uniqueEndpoints = new Set<string>();
      transactionData.slice(0, 20).forEach(t => {
        if (t.sourceStartpoint) uniqueEndpoints.add(`source:${t.sourceStartpoint}`);
      });

      for (const endpoint of uniqueEndpoints) {
        if (endpointScoresCache.has(endpoint)) continue;

        try {
          const [type, url] = endpoint.split(':');
          const scores = await getEndpointScores(token, configId, url, undefined);

          setEndpointScoresCache(prev => {
            const newCache = new Map(prev);
            if (scores.sourceStartpoint) {
              newCache.set(endpoint, scores.sourceStartpoint);
            }
            return newCache;
          });
        } catch (error) {
          console.debug('Failed to fetch endpoint score:', error);
        }
      }
    };

    fetchEndpointScores();
  }, [transactionData, configId]);

  // Filter transactions based on search and time slider
  const filteredTransactions = transactionData.filter(transaction => {
    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      const matchesSearch = (
        (transaction.transactionId ?? '').toString().toLowerCase().includes(searchLower) ||
        (transaction.status ?? '').toString().toLowerCase().includes(searchLower) ||
        transaction.sourceStartpoint?.toLowerCase().includes(searchLower) ||
        JSON.stringify(parsePayload(transaction)).toLowerCase().includes(searchLower)
      );
      if (!matchesSearch) return false;
    }
    
    // Apply time slider filter
    const transactionDate = transaction.startDate ? dayjs(transaction.startDate) : dayjs(0);
    const transactionHour = transactionDate.hour();
    
    // Check if transaction falls within the selected time range
    if (transactionHour < timeSliderRange[0] || transactionHour > timeSliderRange[1]) {
      return false;
    }
    
    return true;
  });

  // Update preview payload when filtered transactions change
  useEffect(() => {
    if (filteredTransactions.length > 0) {
      const firstTransaction = filteredTransactions[0];
      setPreviewPayload({
        transactionId: firstTransaction.transactionId,
        timestamp: firstTransaction.startDate,
        status: firstTransaction.status,
        payload: parsePayload(firstTransaction)
      });
    } else {
      setPreviewPayload(null);
    }
  }, [filteredTransactions.length, timeSliderRange, searchText]);

  // Table columns
  const columns: ColumnsType<Transaction> = [
    {
      title: (
        <Checkbox
          checked={selectedTransactions.length === transactionData.length && transactionData.length > 0}
          indeterminate={selectedTransactions.length > 0 && selectedTransactions.length < transactionData.length}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      key: 'select',
      width: 50,
      render: (_: any, record: Transaction) => (
        <Checkbox
          checked={selectedTransactions.some(t => t.transactionId === record.transactionId)}
          onChange={(e) => handleSelectTransaction(record, e.target.checked)}
        />
      ),
    },
    {
      title: 'Transaction ID',
      dataIndex: 'transactionId',
      key: 'transactionId',
      ellipsis: true,
      width: 200,
      render: (text: string) => {
        if (text == null || typeof text !== 'string') return '-';
        return (
          <Tooltip title={text}>
            <Text code>{text.substring(0, 12)}...</Text>
          </Tooltip>
        );
      }
    },
    {
      title: 'Timestamp',
      dataIndex: 'startDate',
      key: 'startDate',
      width: 180,
      render: (text: string) => (text != null ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-'),
      sorter: (a: Transaction, b: Transaction) =>
        (a.startDate ? new Date(a.startDate).getTime() : 0) - (b.startDate ? new Date(b.startDate).getTime() : 0),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (text: string) => {
        if (text == null || text === '') return '-';
        return (
          <Tag color={text === 'success' ? 'green' : text === 'error' ? 'red' : 'orange'}>
            {String(text).toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Source',
      dataIndex: 'sourceStartpoint',
      key: 'sourceStartpoint',
      ellipsis: true,
      width: 200,
      render: (text: string, record: Transaction) => {
        if (!text) return '-';
        const cacheKey = `source:${text}`;
        const score = endpointScoresCache.get(cacheKey);
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span>{text}</span>
            {score && renderEndpointScoreBadge(score)}
          </div>
        );
      },
    },
    {
      title: 'Size',
      dataIndex: 'payloadSize',
      key: 'payloadSize',
      width: 100,
      render: (size: number) => size ? `${(size / 1024).toFixed(2)} KB` : 'N/A'
    },
    {
      title: 'Edited',
      key: 'edited',
      width: 80,
      render: (_: any, record: Transaction) => (
        editedPayloads.has(record.transactionId) ? (
          <Tooltip title="This transaction has been edited">
            <Tag color="orange" icon={<EditOutlined />}>Modified</Tag>
          </Tooltip>
        ) : null
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: any, record: Transaction) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditTransaction(record)}
          >
            Edit
          </Button>
          {editedPayloads.has(record.transactionId) && (
            <Button
              type="link"
              size="small"
              danger
              onClick={() => handleRemoveEdit(record.transactionId)}
            >
              Reset
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className={`data-replay-enhanced ${isDarkMode ? 'dark' : 'light'}`}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title level={4}>
              <PlayCircleOutlined /> Data Replay
            </Title>
            <Text type="secondary">
              Select transactions, edit payloads if needed, and replay them through your integration pipeline
            </Text>
          </div>

          <Alert
            message="Important Information"
            description="Data replay will re-insert selected transactions into the source Kafka topic. Edited data will be used instead of original payload. Use caution in production environments."
            type="warning"
            showIcon
            icon={<WarningOutlined />}
          />

          {/* Statistics */}
          <Row gutter={16}>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="Available Transactions" 
                  value={transactionData.length}
                  prefix={<InfoCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="Selected" 
                  value={selectedTransactions.length}
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="Edited" 
                  value={editedPayloads.size}
                  valueStyle={{ color: '#cf1322' }}
                  prefix={<EditOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="Ready to Replay" 
                  value={selectedTransactions.length}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Advanced Filtering Section */}
          <Card title={<><FilterOutlined /> Data Selection & Filtering</>} size="small">
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Text strong>Date Range:</Text>
                  <RangePicker
                    style={{ width: '100%', marginTop: 8 }}
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    showTime={{ format: 'HH:mm' }}
                    format="YYYY-MM-DD HH:mm"
                    placeholder={['Start Date', 'End Date']}
                  />
                </Col>
                <Col span={12}>
                  <Text strong>Search Transactions:</Text>
                  <Input
                    style={{ marginTop: 8 }}
                    placeholder="Search by ID, status, source, or payload content..."
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                  />
                </Col>
              </Row>
              
              <Row gutter={16}>
                <Col span={16}>
                  <div>
                    <Row align="middle">
                      <Col flex="auto">
                        <Text strong><ClockCircleOutlined /> Time Range Slider (Hours):</Text>
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                          {timeSliderRange[0]}:00 - {timeSliderRange[1]}:00
                        </Text>
                      </Col>
                    </Row>
                    <Slider
                      range
                      min={0}
                      max={24}
                      value={timeSliderRange}
                      onChange={handleTimeSliderChange}
                      marks={{
                        0: '00:00',
                        6: '06:00',
                        12: '12:00',
                        18: '18:00',
                        24: '24:00'
                      }}
                      style={{ marginTop: 8 }}
                      tooltip={{
                        formatter: (value) => `${value}:00`
                      }}
                    />
                  </div>
                </Col>

                {/* Payload Preview Window */}
                <Col span={8}>
                  <Card 
                    size="small" 
                    title={
                      <Space>
                        <EyeOutlined />
                        <Text strong>First Payload Preview</Text>
                      </Space>
                    }
                    style={{
                      height: '160px',
                      border: isDarkMode ? '1px solid #434343' : '1px solid #d9d9d9'
                    }}
                  >
                    {previewPayload ? (
                      <div style={{ height: '90px', overflow: 'auto' }}>
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Text type="secondary" style={{ fontSize: '11px', wordBreak: 'break-all' }}>
                          <strong>ID:</strong> {previewPayload.transactionId}
                        </Text>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          <strong>Time:</strong> {dayjs(previewPayload.timestamp).format('HH:mm:ss')}
                        </Text>
                        <Tag color={previewPayload.status === 'success' ? 'green' : 'orange'} style={{ fontSize: '10px' }}>
                          {previewPayload.status}
                        </Tag>
                      </Space>
                        <Divider style={{ margin: '8px 0' }} />
                        <pre style={{
                          fontSize: '10px',
                          margin: 0,
                          maxHeight: '45px',
                          overflow: 'auto',
                          background: isDarkMode ? '#1f1f1f' : '#fff',
                          color: isDarkMode ? '#fff' : '#000',
                          padding: '4px',
                          borderRadius: '2px',
                          border: isDarkMode ? '1px solid #333' : '1px solid #e8e8e8'
                        }}>
                          {(previewPayload.payload != null ? JSON.stringify(previewPayload.payload, null, 1) : '').substring(0, 150)}...
                        </pre>
                      </div>
                    ) : (
                      <div style={{ 
                        height: '90px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center' 
                      }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          No transactions in selected range
                        </Text>
                      </div>
                    )}
                  </Card>
                </Col>
              </Row>

              <Space>
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setFilterDrawerVisible(true)}
                >
                  Advanced Filters
                </Button>
                <Button
                  onClick={handleResetFilters}
                >
                  Reset Filters
                </Button>
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  Showing {filteredTransactions.length} of {transactionData.length} transactions
                </Text>
              </Space>
            </Space>
          </Card>

          {/* Action Buttons */}
          <Space>
            <Button
              type="primary"
              size="large"
              icon={<PlayCircleOutlined />}
              onClick={handleStartReplay}
              disabled={selectedTransactions.length === 0 || isLoading}
            >
              Start Replay ({selectedTransactions.length})
            </Button>
            <Button
              size="large"
              icon={<ToolOutlined />}
              onClick={() => setBulkEditDrawerVisible(true)}
              disabled={selectedTransactions.length === 0}
            >
              Bulk Edit Workbench ({selectedTransactions.length})
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchTransactions(
                  configId,
                  1,
                  5000,
                  dateFilter?.startDate || dayjs().subtract(7, 'day').toISOString(),
                  dateFilter?.endDate || dayjs().toISOString(),
                  true
                );
              }}
              loading={isLoading}
            >
              Refresh Data
            </Button>
          </Space>

          {/* Transactions Table */}
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" tip="Loading transactions..." />
            </div>
          ) : (
            <Table
              dataSource={filteredTransactions}
              columns={columns}
              rowKey={(record) => record.transactionId ?? record.startDate ?? String(Math.random())}
              pagination={{
                pageSize: 20,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} transactions`,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100']
              }}
              scroll={{ x: 'max-content' }}
              size="middle"
            />
          )}
        </Space>
      </Card>

      {/* Edit Modal */}
      <Modal
        title={
          <Space>
            <EditOutlined />
            <span>Edit Transaction Payload</span>
          </Space>
        }
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={handleSaveEdit}
        width={800}
        okText="Save Changes"
        cancelText="Cancel"
      >
        {currentEditTransaction && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Alert
              message="Transaction Details"
              description={
                <Space direction="vertical" size="small">
                  <Text><strong>ID:</strong> {currentEditTransaction.transactionId}</Text>
                  <Text><strong>Timestamp:</strong> {dayjs(currentEditTransaction.startDate).format('YYYY-MM-DD HH:mm:ss')}</Text>
                  <Text><strong>Source:</strong> {currentEditTransaction.sourceStartpoint}</Text>
                </Space>
              }
              type="info"
              showIcon
            />
            
            <div>
              <Text strong>Edit Payload (JSON):</Text>
              <div style={{ marginTop: 8, border: '1px solid #d9d9d9', borderRadius: '4px', overflow: 'hidden' }}>
                <MonacoEditor
                  key={`editor-${isDarkMode ? 'dark' : 'light'}`}
                  height="400px"
                  language="json"
                  theme={isDarkMode ? 'vs-dark' : 'vs-light'}
                  value={editedPayload}
                  onChange={(value) => setEditedPayload(value || '{}')}
                  options={{
                    minimap: { enabled: false },
                    fontSize: 14,
                    formatOnPaste: true,
                    formatOnType: true,
                  }}
                />
              </div>
            </div>
          </Space>
        )}
      </Modal>

      {/* Replay Confirmation Modal */}
      <Modal
        title={
          <Space>
            <PlayCircleOutlined />
            <span>Replay Confirmation</span>
          </Space>
        }
        open={replayModalVisible}
        onCancel={handleCloseReplayModal}
        footer={replayStep === 4 ? [
          <Button key="close" type="primary" onClick={handleCloseReplayModal}>
            Close
          </Button>
        ] : [
          <Button key="cancel" onClick={handleCloseReplayModal} disabled={replayInProgress}>
            Cancel
          </Button>,
          <Button 
            key="replay" 
            type="primary" 
            onClick={handleExecuteReplay}
            loading={replayInProgress}
            disabled={replayInProgress}
          >
            Execute Replay
          </Button>
        ]}
        width={700}
        closable={!replayInProgress}
        maskClosable={!replayInProgress}
      >
        {replayStep === 0 && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Alert
              message="Review Before Replay"
              description="Please review the details below before starting the replay process."
              type="warning"
              showIcon
            />

            <Card title="Replay Summary">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic title="Total Transactions" value={selectedTransactions.length} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="Modified Transactions" value={editedPayloads.size} />
                  </Col>
                </Row>
                
                <Paragraph>
                  <Text strong>Action:</Text> The selected transactions will be re-inserted into the source Kafka topic for integration <Text code>{configId}</Text>
                </Paragraph>

                {editedPayloads.size > 0 && (
                  <Alert
                    message={`${editedPayloads.size} transaction(s) have been modified and will use the edited payload instead of the original.`}
                    type="info"
                    showIcon
                  />
                )}
              </Space>
            </Card>

            <Alert
              message="Production Warning"
              description="Replaying data in production environments can affect live systems. Ensure you understand the impact before proceeding."
              type="error"
              showIcon
            />
          </Space>
        )}

        {replayStep > 0 && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Steps current={replayStep - 1} status={replayInProgress ? 'process' : 'finish'}>
              <Step title="Prepare" description="Preparing data" />
              <Step title="Send" description="Sending to Kafka" />
              <Step title="Process" description="Processing results" />
              <Step title="Complete" description="Finished" />
            </Steps>

            {replayInProgress && (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <Spin size="large" />
                <Paragraph style={{ marginTop: 16 }}>
                  {replayStep === 1 && 'Preparing replay data...'}
                  {replayStep === 2 && 'Sending transactions to Kafka topic...'}
                  {replayStep === 3 && 'Processing replay results...'}
                </Paragraph>
              </div>
            )}

            {replayResults && (
              <Card title="Replay Results" style={{ marginTop: 16 }}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic 
                      title="Total" 
                      value={replayResults.total}
                      prefix={<InfoCircleOutlined />}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic 
                      title="Successful" 
                      value={replayResults.successful}
                      valueStyle={{ color: '#3f8600' }}
                      prefix={<CheckCircleOutlined />}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic 
                      title="Failed" 
                      value={replayResults.failed}
                      valueStyle={{ color: '#cf1322' }}
                      prefix={<WarningOutlined />}
                    />
                  </Col>
                </Row>

                {replayResults.messages && replayResults.messages.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <Text strong>Messages:</Text>
                    <ul>
                      {replayResults.messages.map((msg: string, idx: number) => (
                        <li key={idx}><Text>{msg}</Text></li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card>
            )}
          </Space>
        )}
      </Modal>

      {/* Bulk Edit Workbench Drawer */}
      <Drawer
        title={<Space><ToolOutlined /> Bulk Edit Workbench</Space>}
        placement="right"
        width={720}
        onClose={() => setBulkEditDrawerVisible(false)}
        open={bulkEditDrawerVisible}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Alert
            message="Bulk Data Transformation"
            description={`Apply transformations to ${selectedTransactions.length} selected transaction(s). Changes will be applied to all selected payloads.`}
            type="info"
            showIcon
          />

          <Tabs activeKey={bulkEditOperation} onChange={setBulkEditOperation}>
            <TabPane tab="Find & Replace" key="find-replace">
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                  <Text strong>Find:</Text>
                  <Input
                    placeholder="Enter text to find"
                    value={findValue}
                    onChange={(e) => setFindValue(e.target.value)}
                    style={{ marginTop: 8 }}
                  />
                </div>
                
                <div>
                  <Text strong>Replace With:</Text>
                  <Input
                    placeholder="Enter replacement text"
                    value={replaceValue}
                    onChange={(e) => setReplaceValue(e.target.value)}
                    style={{ marginTop: 8 }}
                  />
                </div>

                <Alert
                  message="Note"
                  description="This will search through the JSON payload and replace all occurrences of the find text with the replacement text."
                  type="warning"
                  showIcon
                />

                <Button
                  type="primary"
                  block
                  onClick={handleBulkFindReplace}
                  icon={<EditOutlined />}
                >
                  Apply Find & Replace
                </Button>
              </Space>
            </TabPane>

            <TabPane tab="JavaScript Transform" key="script">
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Alert
                  message="JavaScript Transformation"
                  description="Write JavaScript code to transform the payload object. The payload is available as the 'payload' variable. Make sure to return the modified payload."
                  type="info"
                  showIcon
                />

                <div>
                  <Text strong>Transformation Script:</Text>
                  <div style={{ marginTop: 8, border: '1px solid #d9d9d9', borderRadius: '4px', overflow: 'hidden' }}>
                    <MonacoEditor
                      key={`bulk-editor-${isDarkMode ? 'dark' : 'light'}`}
                      height="300px"
                      language="javascript"
                      theme={isDarkMode ? 'vs-dark' : 'vs-light'}
                      value={bulkEditScript}
                      onChange={(value) => setBulkEditScript(value || '')}
                      options={{
                        minimap: { enabled: false },
                        fontSize: 14,
                      }}
                    />
                  </div>
                </div>

                <Card size="small" title="Example Scripts">
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <Button
                      size="small"
                      block
                      onClick={() => setBulkEditScript("// Add a new field\npayload.newField = 'value';")}
                    >
                      Add Field
                    </Button>
                    <Button
                      size="small"
                      block
                      onClick={() => setBulkEditScript("// Update a field\npayload.status = 'modified';")}
                    >
                      Update Field
                    </Button>
                    <Button
                      size="small"
                      block
                      onClick={() => setBulkEditScript("// Delete a field\ndelete payload.unwantedField;")}
                    >
                      Delete Field
                    </Button>
                    <Button
                      size="small"
                      block
                      onClick={() => setBulkEditScript("// Transform nested data\nif (payload.data) {\n  payload.data.timestamp = new Date().toISOString();\n}")}
                    >
                      Transform Nested
                    </Button>
                  </Space>
                </Card>

                <Button
                  type="primary"
                  block
                  onClick={handleBulkApplyScript}
                  icon={<PlayCircleOutlined />}
                  danger
                >
                  Execute Script on All Selected
                </Button>
              </Space>
            </TabPane>
          </Tabs>

          <Divider />

          <Space>
            <Button onClick={() => setBulkEditDrawerVisible(false)}>
              Cancel
            </Button>
            <Button
              danger
              onClick={() => {
                setEditedPayloads(new Map());
                message.success('All edits cleared');
                setBulkEditDrawerVisible(false);
              }}
            >
              Clear All Edits
            </Button>
          </Space>
        </Space>
      </Drawer>

      {/* Advanced Filter Drawer */}
      <Drawer
        title={<Space><FilterOutlined /> Advanced Filters</Space>}
        placement="right"
        width={500}
        onClose={() => setFilterDrawerVisible(false)}
        open={filterDrawerVisible}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Alert
            message="Advanced Filtering"
            description="Use these filters to narrow down your transaction selection for replay."
            type="info"
            showIcon
          />

          <Form layout="vertical" form={form}>
            <Form.Item label="Transaction Status">
              <Select
                mode="multiple"
                placeholder="Select statuses"
                options={[
                  { label: 'Success', value: 'success' },
                  { label: 'Error', value: 'error' },
                  { label: 'Pending', value: 'pending' }
                ]}
              />
            </Form.Item>

            <Form.Item label="Payload Size Range (KB)">
              <Slider
                range
                min={0}
                max={1000}
                defaultValue={[0, 1000]}
              />
            </Form.Item>

            <Form.Item label="Source Filter">
              <Input placeholder="Filter by source endpoint" />
            </Form.Item>

            <Form.Item label="Destination Filter">
              <Input placeholder="Filter by destination endpoint" />
            </Form.Item>
          </Form>

          <Space>
            <Button type="primary" onClick={handleApplyFilters}>
              Apply Filters
            </Button>
            <Button onClick={handleResetFilters}>
              Reset All
            </Button>
            <Button onClick={() => setFilterDrawerVisible(false)}>
              Close
            </Button>
          </Space>
        </Space>
      </Drawer>
    </div>
  );
};

export default DataReplayEnhanced;

